var m = require('mithril');
var prop = require('mithril/stream');
var key = require('../../libs/keybindings');
var Draggable = require('../../libs/draggable');
var Element = require('../../dashboards/models/element');
var loaders = require('../../layout/components/loaders');
var modal = require('../../layout/components/modal');
var edit = require('../../layout/components/edit');
var columnGroups = require('./columnGroups');
var modalForm = require('../../layout/components/modalForm');
var elementForm = modalForm(require('../../dashboards/models/element'));
var helpModal = require('../../help/helpModal');
var sortRecovery = require('./sortRecovery');
var unnamedColumnLabel = 'Unnamed column';

function normalizeColumnName (name) {
  return String(name || '').trim();
}

function displayColumnName (name) {
  return normalizeColumnName(name);
}

function columnId (column, idx) {
  return typeof column == 'string' || typeof column.id == 'undefined' ? idx : column.id;
}

function columnKey (column, idx) {
  var id = columnId(column, idx);
  return typeof id + ':' + String(id);
}

function columnIndex (column, idx) {
  return typeof column == 'string' || typeof column.index == 'undefined' ? idx : column.index;
}

function columnName (column) {
  return typeof column == 'string' ? column : (column.name || '');
}

function columnType (column) {
  return typeof column == 'string' ? 'point' : (column.type || 'point');
}

function columnTextAlign (column) {
  return typeof column == 'string' ? 'left' : (column.textAlign || 'left');
}

function editColumnHandle (name) {
  var label = displayColumnName(name);
  return label || m('i.ion-arrow-move', {'aria-hidden': 'true'});
}

function normalizeColumnRecord (column, idx) {
  if (typeof column == 'string') {
    return {name: normalizeColumnName(column), id: idx, type: 'point', index: idx};
  }

  if (typeof column.name == 'undefined') {
    column.name = '';
  }
  if (typeof column.id == 'undefined') {
    column.id = idx;
  }
  if (typeof column.index == 'undefined') {
    column.index = idx;
  }
  if (!column.type) {
    column.type = 'point';
  }
  return column;
}

function prepareRecordColumns (record) {
  if (!record) {
    return record;
  }
  if (!Array.isArray(record.columns)) {
    record.columns = [];
  }

  record.columns = fixRepeatedIndexes(record.columns.map(normalizeColumnRecord));
  return record;
}

function reinitializeParent (ctrl) {
  if (typeof ctrl.parentReinitialize === 'function') {
    return Promise.resolve(ctrl.parentReinitialize());
  }
  return Promise.resolve();
}

function hasColumnIdentityValue (value) {
  return value !== null && typeof value !== 'undefined' && value !== '';
}

function numericColumnValue (value) {
  if (!hasColumnIdentityValue(value)) {
    return null;
  }

  var numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
}

function nextNumericColumnId (columns) {
  var maxNumericId = -1;
  columns.forEach(function (column, idx) {
    var numericId = numericColumnValue(columnId(column, idx));
    if (numericId !== null && numericId > maxNumericId) {
      maxNumericId = numericId;
    }
  });
  return maxNumericId + 1;
}

function nextColumnIndex (columns) {
  var maxNumericIndex = -1;
  columns.forEach(function (column, idx) {
    var numericIndex = numericColumnValue(columnIndex(column, idx));
    if (numericIndex !== null && numericIndex > maxNumericIndex) {
      maxNumericIndex = numericIndex;
    }
  });
  return maxNumericIndex + 1;
}

function getElementAttributeValue (element, attributeName) {
  if (!element) {
    return undefined;
  }

  if (typeof element.getAttribute === 'function') {
    var attributeValue = element.getAttribute(attributeName);
    if (attributeValue !== null) {
      return attributeValue;
    }
  }

  if (element.attributes && element.attributes[attributeName]) {
    return element.attributes[attributeName].value;
  }

  var lowerAttributeName = attributeName.toLowerCase();
  if (element.attributes && element.attributes[lowerAttributeName]) {
    return element.attributes[lowerAttributeName].value;
  }

  return undefined;
}

function findColumnIndex (columns, usedColumnIndexes, predicate) {
  for (var idx = 0; idx < columns.length; idx += 1) {
    if (usedColumnIndexes[idx]) {
      continue;
    }
    if (predicate(columns[idx], idx)) {
      return idx;
    }
  }
  return -1;
}

function findOriginalColumnIndex (columns, columnElement, usedColumnIndexes) {
  var columnElementId = getElementAttributeValue(columnElement, 'id');
  var columnElementIndex = getElementAttributeValue(columnElement, 'index');
  var columnElementName = getElementAttributeValue(columnElement, 'name') || '';
  var columnElementType = getElementAttributeValue(columnElement, 'type') || 'point';
  var columnElementTextAlign = getElementAttributeValue(columnElement, 'textAlign') || 'left';
  var originalColumnIndex = -1;

  if (hasColumnIdentityValue(columnElementId)) {
    originalColumnIndex = findColumnIndex(columns, usedColumnIndexes, function (column, idx) {
      var id = columnId(column, idx);
      return hasColumnIdentityValue(id) && String(id) === String(columnElementId);
    });
  }

  if (originalColumnIndex === -1 && hasColumnIdentityValue(columnElementIndex)) {
    originalColumnIndex = findColumnIndex(columns, usedColumnIndexes, function (column, idx) {
      var index = columnIndex(column, idx);
      return hasColumnIdentityValue(index) && String(index) === String(columnElementIndex);
    });
  }

  if (originalColumnIndex === -1) {
    originalColumnIndex = findColumnIndex(columns, usedColumnIndexes, function (column) {
      return displayColumnName(columnName(column)) === columnElementName &&
        columnType(column) === columnElementType &&
        columnTextAlign(column) === columnElementTextAlign;
    });
  }

  if (originalColumnIndex === -1) {
    originalColumnIndex = findColumnIndex(columns, usedColumnIndexes, function () {
      return true;
    });
  }

  return originalColumnIndex;
}

function columnForSortedPosition (column, originalIndex, sortedIndex) {
  var normalizedColumn = normalizeColumnRecord(column, originalIndex);
  return Object.assign({}, normalizedColumn, {index: sortedIndex});
}

function columnElementForSortedPosition (columnElement, sortedIndex) {
  var column = {
    name: getElementAttributeValue(columnElement, 'name') || '',
    id: getElementAttributeValue(columnElement, 'id'),
    index: sortedIndex,
    type: getElementAttributeValue(columnElement, 'type') || 'point'
  };
  var textAlign = getElementAttributeValue(columnElement, 'textAlign');
  if (textAlign) {
    column.textAlign = textAlign;
  }
  return column;
}

var component = {
  oninit: function oninit (vnode) {
    var ctrl = this;
    ctrl.name = prop('');
    ctrl.textAlign = prop('');
    ctrl.type = prop('point');
    ctrl.options = vnode.attrs.options;
    ctrl.parentReinitialize = vnode.attrs.parentReinitialize;
    ctrl.dashboardCallback = vnode.attrs.dashboardCallback;
    ctrl.hidePreferredViewMenu = vnode.attrs.hidePreferredViewMenu;
    ctrl.sortError = null;
    ctrl.reloadAfterSortError = function () {
      return Promise.resolve()
        .then(function () { return reinitializeParent(ctrl); })
        .then(function () {
          ctrl.sortError = null;
          m.redraw();
        })
        .catch(function (error) {
          console.error('Unable to reload table after a column order failure:', error);
          return null;
        });
    };
    ctrl.elementRecord = vnode.attrs.elementRecord;
    ctrl.getRecord = function () {
      return prepareRecordColumns(ctrl.elementRecord && ctrl.elementRecord());
    };

    var record = ctrl.getRecord();
    if (!record) {
      return;
    }
    
    ctrl.addColumn = function () {
      ctrl.name('');
      ctrl.textAlign('left');
      ctrl.type('point');
      modal.vm.title('New Column');
      modal.vm.body = formViewAdd.bind(ctrl, ctrl);

      var submit = function () {
        var record = ctrl.getRecord();
        var name = normalizeColumnName(ctrl.name());
        var newId = nextNumericColumnId(record.columns);
        var newIndex = nextColumnIndex(record.columns);
        record.columns.push({name: name, id: newId, type: ctrl.type(), index: newIndex});
        return ctrl.putRecord(record)
        .then(function () {
          key.unbind('enter');
          return reinitializeParent(ctrl);
        })
        .then(function () {
          modal.vm.close();
        })
      }

      modal.vm.buttons([
        {
          label: "Submit",
          class: "dib ",
          func: submit
        }
      ])

      key.setScope('modal');
      key('enter', 'modal', submit);

      modal.vm.open();
    };

    ctrl.sortChildren = (columnElements) => {
      var elementRecord = ctrl.getRecord();
      if (!elementRecord || !elementRecord['@rid']) {
        return Promise.resolve();
      }

      var usedColumnIndexes = {};
      var sourceColumns = Array.isArray(elementRecord.columns) ? elementRecord.columns : [];
      var updatedColumns = (columnElements || []).map(function (columnElement, sortedIndex) {
        var originalColumnIndex = findOriginalColumnIndex(sourceColumns, columnElement, usedColumnIndexes);
        if (originalColumnIndex === -1) {
          return columnElementForSortedPosition(columnElement, sortedIndex);
        }
        usedColumnIndexes[originalColumnIndex] = true;
        return columnForSortedPosition(sourceColumns[originalColumnIndex], originalColumnIndex, sortedIndex);
      });

      elementRecord.columns = updatedColumns;
      m.redraw();

      return sortRecovery.persistSortOrder({
        context: 'table columns',
        message: 'Unable to reorder table columns. The latest saved order was reloaded.',
        request: function () {
          return loaders.requestWithLoader({
            loader: 'background',
            method: 'PUT',
            url: '/api/v1/element/' + elementRecord['@rid'].slice(1),
            body: {
              columns: updatedColumns
            }
          });
        },
        reload: function () { return reinitializeParent(ctrl); },
        onSuccess: function () {
          ctrl.sortError = null;
        },
        onError: function (message) {
          ctrl.sortError = message;
          m.redraw();
        }
      });
    };

    ctrl.editColumn = function (elementIndex) {
      var record = ctrl.getRecord();
      var columnRecord = record.columns[elementIndex];
      ctrl.name(columnName(columnRecord));
      ctrl.textAlign(columnTextAlign(columnRecord));
      modal.vm.title('Edit Column Name');
      modal.vm.body = formViewEdit.bind(ctrl, ctrl);

      var submit = function () {
        var record = ctrl.getRecord();
        var name = normalizeColumnName(ctrl.name());

        if (typeof record.columns[elementIndex] == 'string') {
          record.columns[elementIndex] = {
            name: name,
            id: elementIndex,
            type: 'point',
            index: elementIndex,
            textAlign: ctrl.textAlign()
          };
        } else {
          record.columns[elementIndex]['name'] = name;
          record.columns[elementIndex]['index'] = elementIndex;
          record.columns[elementIndex]['textAlign'] = ctrl.textAlign();
        }
        return ctrl.putRecord(record)
        .then(function () {
          key.unbind('enter');
          return reinitializeParent(ctrl);
        })
        .then(function () {
          modal.vm.close();
        })
      }

      modal.vm.buttons([
        {
          label: "Delete",
          class: "dib red mr3 ",
          func: ctrl.deleteColumn.bind(ctrl, elementIndex)
        },
        {
          label: "Submit",
          class: "dib ",
          func: submit
        }
      ])

      key.setScope('modal');
      key('enter', 'modal', submit);

      modal.vm.open();
    };

    ctrl.deleteColumn = function (elementIndex) {
      modal.vm.title('Delete Column');
      modal.vm.body = prop(m('p', 'Are you sure?'));

      var submit = function () {
        var record = ctrl.getRecord();
        record.columns.splice(elementIndex, 1);
        return ctrl.putRecord(record)
        .then(function () {
          key.unbind('enter');
          return reinitializeParent(ctrl);
        })
        .then(function () {
          modal.vm.close();
        })
      }
      modal.vm.buttons([
        {
          label: "Submit",
          class: "dib ",
          func: submit
        }
      ])

      key.setScope('modal');
      key('enter', 'modal', submit);

      modal.vm.open();
    };

    ctrl.putRecord = function (recordToSave) {
      var record = recordToSave || ctrl.getRecord();
      return loaders.requestWithLoader({
        loader: 'background',
        url: Element.getUrl(record),
        method: 'PUT',
        body: {columns: record.columns}
      });
    };
  },
  onupdate: function onupdate (vnode) {
    this.options = vnode.attrs.options;
    this.parentReinitialize = vnode.attrs.parentReinitialize;
    this.dashboardCallback = vnode.attrs.dashboardCallback;
    this.hidePreferredViewMenu = vnode.attrs.hidePreferredViewMenu;
    this.elementRecord = vnode.attrs.elementRecord;
    if (this.getRecord) {
      this.getRecord();
    }
  },
  view: function view (vnode) {
    var ctrl = this
    ctrl.elementRecord = vnode.attrs.elementRecord || ctrl.elementRecord;
    var record = ctrl.getRecord ? ctrl.getRecord() : prepareRecordColumns(vnode.attrs.elementRecord && vnode.attrs.elementRecord());
    if (!record) {
      return m('thead', []);
    }
    var borderMap = getColumnGroupBorders(record.columnGroups);
    if (!edit.vm.editing()) {
      return m('thead', [
        record.columnGroups ? m(columnGroups,{tableRecord: record}) : '',
        m('tr', [
          m('th.v-btm.pv1.bb.f1.fw5.tl.bw1.pr3', [
            m('div.flex.justify-between.items-end', [
              record.name,
              record.helpText ?
                m('i.ion-ios-information-outline.white.f1.ml2', {
                  onclick: () => {
                    helpModal.editDialogue(record.name, undefined, record.helpText);
                  }
                })
              : ''
          ])
        ]),
        record.columns.map(function (column, idx) {
          return m('th.tl.v-btm.bb.fw2.bw1.ph1', {class: borderMap[idx]}, displayColumnName(columnName(column)));
        }),
          m('th.v-btm.bb.bw1.pv1')
      ])
      ])
    }

    return m('thead', [
      ctrl.sortError ? m('tr', [
        m('th.dark-red.tl', { colspan: (record.columns || []).length + 2 }, [
          m('span', ctrl.sortError + ' '),
          m('button', { type: 'button', onclick: ctrl.reloadAfterSortError }, 'Refresh')
        ])
      ]) : null,
      m(columnGroups, {tableRecord: record, parentReinitialize: ctrl.parentReinitialize}),
      m(Draggable, {onsort: ctrl.sortChildren, draggable: '.th-drag', handleClass:'.th-drag-handle', htmlElement: 'tr'}, [
        m('th.v-btm.bb.f1.fw1.tl.bw1.pr3', [
          m('div.f1.bw1.flex.justify-between.items-centernarrow.grid-item-drag-handle.pv0', {style:{cursor: 'grab'}}, [
            record.name ? 
              m('span.blue', {onclick: elementForm.editDialogue.bind(elementForm, record , {delete:'true', move:'true', submitCallback: ctrl.parentReinitialize, dashboardCallback: ctrl.dashboardCallback})}, record.name)
            :
              m('i.ion-arrow-move.blue.f3', {onclick: elementForm.editDialogue.bind(elementForm, record , {delete:'true', move:'true', submitCallback: ctrl.parentReinitialize, dashboardCallback: ctrl.dashboardCallback})})
          ])
        ]),
        (ctrl.options && ctrl.options.preColumns) ? ctrl.options.preColumns : [],
        record.columns.map(function (column, idx) {
          var name = columnName(column);
          var label = displayColumnName(name) || unnamedColumnLabel;
          return m('th.tl.v-btm.bb.fw2.bw1.ph1.th-drag', {
            class: borderMap[idx],
            id: columnId(column, idx),
            index: columnIndex(column, idx),
            name: displayColumnName(name),
            type: columnType(column),
            textAlign: columnTextAlign(column),
            key: columnKey(column, idx)
          }, [
            m('a.blue.th-drag-handle', {
              onclick: ctrl.editColumn.bind(null, idx),
              title: label,
              'aria-label': label
            }, editColumnHandle(name))
          ]);
        }),
        m('th.bt-0.bl-0.br-0.bb-0.b--dotted.bw1.tl', [
          m('i.icon.ion-plus.blue.dib.ml2', {onclick: ctrl.addColumn.bind(null, {out: record['@rid']})})
        ]),
      ])
    ])
  }
};


function formViewAdd (ctrl) {
  return m("form", {role:"form"}, [
    m("div", {class:"form-group"}, [
      m("name", {for:"element-name-input"}, "Name"),
      m("input.form-control", {
        type:"text", 
        autocomplete: 'off',
        oninput: function(ev) {ctrl.name(ev.target.value)},
        value: ctrl.name(),
      }),
    ]),
    m("div", {class:"form-group"}, [
      m("name", {for:"element-type-input"}, "Type"),
      m("select.form-control", {
        onchange: function(ev) {ctrl.type(ev.target.value)},
        value: ctrl.type(),
      }, [
        m("option", {value:"point"}, "Live Value"),
        m("option", {value:"number"}, "Number"),
        m("option", {value:"string"}, "Text")
      ]),
    ])
  ]);
}

function formViewEdit (ctrl) {
  return m("form", {role:"form"}, [
    m("div", {class:"form-group"}, [
      m("name", {for:"element-name-input"}, "Name"),
      m("input.form-control", {
        type:"text", 
        autocomplete: 'off',
        oninput: function(ev) {ctrl.name(ev.target.value)},
        value: ctrl.name(),
      }),
      m("name", "Text Align"),
      m("select.form-control", {
        value: ctrl.textAlign(), 
        onchange: function(ev) {ctrl.textAlign(ev.target.value)}
      }, [
        m("option", {value:'center'}, 'Center'),
        m("option", {value:'left'}, 'Left'),
        m("option", {value:'right'}, 'Right')
      ])

    ])
  ]);
}

function getColumnGroupBorders (columnGroups) {
  if (!Array.isArray(columnGroups)) {
    return {};
  }
  var borderMap = {};
  var columnIndex = 0;
  columnGroups.forEach(function (columnGroup, idx) {
    if (columnGroup.width < 1) {
      columnIndex += 1;
      return
    }
    borderMap[columnIndex] = 'bl';
    borderMap[columnIndex + Number(columnGroup.width) - 1] = 'br';
    columnIndex += Number(columnGroup.width);
  })
  return borderMap;
}

function arraymove(arr, fromIndex, toIndex) {
    var element = arr[fromIndex];
    arr.splice(fromIndex, 1);
    arr.splice(toIndex, 0, element);
}


function fixRepeatedIndexes(array) {
  const indexCount = {};
  
  // Count the occurrences of each index
  for (const obj of array) {
      const index = obj.index;
      if (index in indexCount) {
          indexCount[index]++;
      } else {
          indexCount[index] = 1;
      }
  }
  
  // Update the objects with repeated indexes
  for (const obj of array) {
      const index = obj.index;
      if (indexCount[index] > 1) {
          let newIndex = index;
          while (indexCount[newIndex] > 0) {
              newIndex++;
          }
          indexCount[newIndex] = 1;
          indexCount[index]--;
          obj.index = newIndex;
      }
  }
  
  return array;
}

module.exports = component;
